/**
 * Pure height math for PerfectScrollbarTable.
 *
 * Extracted so the bulk-bar reservation added in CON-1656 can be unit tested
 * — in particular the guarantee that it is a NO-OP for the ~60 pages that use
 * the scroller without a bulk action bar.
 *
 * The scroll area is sized to fill the viewport down to the bottom edge, minus
 * every piece of chrome that sits below or above it. A bulk action bar is a
 * position: fixed Drawer, so it cannot be accounted for by layout and has to
 * be measured the same way.
 */

const MIN_SCROLL_HEIGHT = 300;

/**
 * How much of the viewport bottom a fixed bar actually covers.
 *
 * A persistent Drawer keeps its paper mounted when closed and slides it out
 * of view by transform, so its offsetHeight still reports the full bar. The
 * rect is what distinguishes "showing" from "translated off-screen":
 *   open   -> rect.top === viewportHeight - height  -> returns height
 *   closed -> rect.top === viewportHeight           -> returns 0
 */
const coveredViewportHeight = (rect, viewportHeight) => {
  if (!rect) return 0;
  const height = rect.height || 0;
  const top = rect.top || 0;
  return Math.max(0, Math.min(height, viewportHeight - top));
};

/**
 * @param {object} args
 * @param {number} args.viewportHeight
 * @param {number} args.scrollBoxOffsetTop     offsetTop — see the note below
 * @param {number} args.scrollBoxViewportTop   getBoundingClientRect().top, or
 *                                             null when it was not measured
 * @param {number} args.paginationHeight   0 when the page has no pagination row
 * @param {number} args.headerHeight       0 when the page has no top-right header
 * @param {object} args.bulkBarRect        null when the page has no bulk bar
 * @param {number} args.subDefaultHeight   per-page fudge factor
 *
 * THE UNIT MISMATCH (measured, not theorised — CON-1656):
 *
 * The legacy formula subtracts `scrollBoxOffsetTop`, but offsetTop is measured
 * from the nearest POSITIONED ANCESTOR, not from the viewport. On the Event
 * Guests tab the scroll box reports offsetTop 35 while actually sitting 311px
 * down the viewport — a 276px undercount. `headerHeight` (71) and the per-page
 * `subDefaultHeight` (180) are hand-tuned constants standing in for that gap;
 * together they cover 251 of the 276, leaving the formula 25px long.
 *
 * That 25px is precisely why the bulk action bar kept overlapping the
 * pagination row no matter how much height was subtracted for it.
 *
 * Rather than re-tune a constant that ~60 other pages depend on, the exact
 * rect-based figure is applied ONLY when a bulk bar is actually covering the
 * viewport, and only ever to SHRINK the result. Pages with no bar take the
 * legacy path untouched. Fixing the mismatch everywhere is its own ticket.
 */
const computeScrollBoxHeightBreakdown = ({
  viewportHeight = 0,
  scrollBoxOffsetTop = 0,
  scrollBoxViewportTop = null,
  paginationHeight = 0,
  headerHeight = 0,
  bulkBarRect = null,
  subDefaultHeight = 0,
}) => {
  const bulkBarHeight = coveredViewportHeight(bulkBarRect, viewportHeight);

  const legacyHeight = viewportHeight
    - scrollBoxOffsetTop
    - paginationHeight
    - headerHeight
    - bulkBarHeight
    - subDefaultHeight;

  const hasViewportTop = scrollBoxViewportTop !== null && scrollBoxViewportTop !== undefined;

  const availableByRect = hasViewportTop
    ? viewportHeight - scrollBoxViewportTop - paginationHeight - bulkBarHeight
    : null;

  // Gated on a bar actually being on screen, so this is a no-op everywhere
  // else. Math.min means it can only ever shrink the legacy result, never
  // push content further down.
  const correctionApplies = bulkBarHeight > 0 && hasViewportTop;
  const beforeFloor = correctionApplies
    ? Math.min(legacyHeight, availableByRect)
    : legacyHeight;

  // THE FLOOR MUST NOT EXCEED THE SPACE THAT EXISTS (measured, CON-1656).
  //
  // A flat 300px minimum asserts "the table shall be at least 300px tall"
  // even when only 273px remain — and the only way to satisfy that is to
  // overflow the viewport, which pushes the pagination row under the fixed
  // bulk bar and out of reach. Measured at high browser zoom: viewport 798,
  // chrome above the table 401, pagination 52, bar 72 -> 273 available, floor
  // forced 300, and exactly 27px of the pagination row ended up covered.
  //
  // The scroll box has its own internal scrollbar, so a shorter box costs a
  // smaller window onto the rows. The floor costs the pagination entirely.
  // Capped at what is actually available, so it can never overflow.
  // FLAT FLOOR — REVERTED FROM THE CAPPED VERSION (production incident,
  // 2026-08-26). Do not re-introduce a floor that adapts to available space
  // without solving mobile first.
  //
  // The capped floor, Math.min(MIN_SCROLL_HEIGHT, availableByRect), honestly
  // reported "there is no room" on a short viewport — and on a phone there
  // genuinely is not, once ~400px of chrome, the pagination row and the bulk
  // bar are subtracted from a 667px screen. It collapsed the table to near
  // zero and users saw a header with no rows.
  //
  // Only the Event Guests and Group Contacts tabs broke, because the
  // empty-state hint keeps the bulk bar permanently on screen there, so the
  // `bulkBarHeight > 0` gate below is always true on those two tabs and never
  // on any other page.
  //
  // Mobile has always relied on PAGE SCROLL to reach rows: the table is
  // deliberately taller than the viewport and the user scrolls to it. A floor
  // that refuses to overflow removes the only thing that made that work.
  //
  // Accepted cost: the 27px pagination overlap at 125% desktop zoom returns.
  // A blank table on mobile is far worse than a partly covered pagination row.
  const effectiveFloor = MIN_SCROLL_HEIGHT;

  const height = Math.max(beforeFloor, MIN_SCROLL_HEIGHT);

  return {
    bulkBarHeight,
    legacyHeight,
    availableByRect,
    correctionApplies,
    beforeFloor,
    effectiveFloor,
    floorApplied: height > beforeFloor,
    height,
  };
};

const computeScrollBoxHeight = (args) => computeScrollBoxHeightBreakdown(args).height;

export {
  computeScrollBoxHeight,
  computeScrollBoxHeightBreakdown,
  coveredViewportHeight,
  MIN_SCROLL_HEIGHT,
};
